<template>
  <section class="w-full">
    <div class="footer-root relative overflow-hidden rounded-3xl text-white">
      <img
        :src="bgUrl"
        alt=""
        width="1366"
        height="484"
        loading="lazy"
        fetchpriority="low"
        decoding="async"
        class="absolute inset-0 h-full w-full rounded-3xl object-cover"
      />

      <!-- Dark overlay -->
      <div class="absolute inset-0 bg-black/25"></div>

      <!-- Glow blobs -->
      <div
        class="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full opacity-40 blur-3xl"
        :style="{
          background:
            'radial-gradient(circle at 30% 30%, #ff2aa6, transparent 60%)',
        }"
      ></div>

      <div
        class="pointer-events-none absolute right-16 top-10 h-80 w-80 rounded-full opacity-35 blur-3xl"
        :style="{
          background:
            'radial-gradient(circle at 40% 40%, #a855f7, transparent 65%)',
        }"
      ></div>

      <div
        class="pointer-events-none absolute -bottom-28 left-10 h-96 w-96 rounded-full opacity-30 blur-3xl"
        :style="{
          background:
            'radial-gradient(circle at 40% 40%, #ff2aa6, transparent 65%)',
        }"
      ></div>

      <!-- ✅ CONTENT LAYER (always full height) -->
      <div
        class="absolute inset-0 z-10 flex items-center justify-center px-6 md:px-14"
      >
        <div class="w-full">
          <!-- =====================
               MOBILE (centered)
               ===================== -->
          <div class="mx-auto w-full max-w-[520px] text-center md:hidden">
            <!-- ✅ constrain ONLY the text area to be narrower than the buttons -->
            <div class="footer-mobile-inner mx-auto">
              <!-- logo + title -->
              <div class="flex items-center justify-center gap-3">
                <img
                  :src="logoUrl"
                  alt="Upfest"
                  width="74"
                  height="63"
                  class="h-9 w-auto"
                  loading="lazy"
                  decoding="async"
                />
                <h3
                  class="footer-mobile-title whitespace-pre-line text-[26px] font-semibold uppercase leading-[1.1] tracking-wide"
                >
                  {{ title }}
                </h3>
              </div>

              <!--
              <p class="mx-auto mt-4 max-w-[420px] text-[14px] leading-6 text-white/85 footer-mobile-subtitle">
                {{ subtitle }}
              </p>
              -->
            </div>

            <div class="mt-7 grid grid-cols-1 gap-3">
              <button
                type="button"
                class="btn-gradient h-12 rounded-2xl px-4 text-sm font-semibold shadow-[0_10px_30px_rgba(0,0,0,0.20)]"
                @click="$emit('primary')"
              >
                {{ primaryText }}
              </button>

              <button
                type="button"
                class="h-12 rounded-2xl bg-white px-4 text-sm font-semibold text-indigo-950 shadow-[0_10px_30px_rgba(0,0,0,0.18)] hover:bg-white/90"
                @click="$emit('secondary')"
              >
                {{ secondaryText }}
              </button>
            </div>
          </div>

          <!-- =====================
               TABLET+ (wide layout)
               ===================== -->
          <div class="hidden md:block">
            <div
              class="mx-auto flex w-full flex-col gap-6 lg:flex-row lg:items-center lg:justify-between"
            >
              <!-- left text -->
              <div class="max-w-full lg:max-w-[720px]">
                <div class="flex items-center gap-4">
                  <img
                    :src="logoUrl"
                    alt="Upfest"
                    width="74"
                    height="63"
                    class="h-10 w-auto"
                    loading="lazy"
                    decoding="async"
                  />
                  <h3
                    class="whitespace-pre-line text-2xl font-extrabold uppercase leading-[1.05] tracking-wide lg:text-3xl"
                  >
                    {{ title }}
                  </h3>
                </div>

                <!--
                <p class="mt-4 max-w-[520px] text-base leading-6 text-white/85">
                  {{ subtitle }}
                </p>
                -->
              </div>

              <!-- right buttons -->
              <div class="flex flex-wrap items-center gap-3 lg:gap-4">
                <button
                  type="button"
                  class="btn-gradient h-11 rounded-2xl px-6 text-sm font-semibold shadow-[0_10px_30px_rgba(0,0,0,0.20)] lg:px-8"
                  @click="$emit('primary')"
                >
                  {{ primaryText }}
                </button>

                <button
                  type="button"
                  class="h-11 rounded-2xl bg-white px-6 text-sm font-semibold text-indigo-950 shadow-[0_10px_30px_rgba(0,0,0,0.18)] hover:bg-white/90 lg:px-8"
                  @click="$emit('secondary')"
                >
                  {{ secondaryText }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- /content layer -->
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue";

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    title?: string;
    subtitle?: string;
    primaryText?: string;
    secondaryText?: string;
    logoImageUrl?: string;
    bgImageUrl?: string;
  }>(),
  {
    title: "",
    subtitle: "",
    primaryText: "",
    secondaryText: "",
    logoImageUrl: "/images/upfest_logo_white.png",
    bgImageUrl: "/images/bg_opening.png",
  },
);

defineEmits<{
  (e: "primary" | "secondary"): void;
}>();

const title = computed(() => props.title || t("footerCta.title"));
const primaryText = computed(
  () => props.primaryText || t("footerCta.createAccount"),
);
const secondaryText = computed(
  () => props.secondaryText || t("footerCta.openApp"),
);
const logoUrl = computed(() => props.logoImageUrl);
const bgUrl = computed(() => props.bgImageUrl);
</script>

<style scoped>
.footer-root {
  min-height: 280px;
}

@media (min-width: 768px) {
  .footer-root {
    min-height: 360px;
  }
}

@media (min-width: 1024px) {
  .footer-root {
    height: 484px;
  }
}

.btn-gradient {
  background: linear-gradient(90deg, #3a0a6a 0%, #b0006b 100%);
}

.btn-gradient:hover {
  filter: brightness(1.05);
}

@media (max-width: 767px) {
  .footer-mobile-inner {
    max-width: 290px;
  }

  .footer-mobile-title {
    font-size: 20px;
    font-weight: 800;
    line-height: 1.08;
    letter-spacing: 0.04em;
  }

  .footer-mobile-subtitle {
    font-size: 12.5px;
    line-height: 1.45;
    max-width: 260px;
  }
}
</style>
